'use client';

import React, { createContext, JSX, useContext, useEffect, useState } from 'react';

import { UserRole } from '@/lib/enums';

interface DashboardViewContextType {
  currentDashboardView: UserRole;
  setCurrentDashboardView: (view: UserRole) => void;
  isDashboardViewSwitchingEnabled: boolean;
  availableViews: UserRole[];
}

const DashboardViewContext = createContext<DashboardViewContextType | undefined>(undefined);

interface DashboardViewProviderProps {
  children: React.ReactNode;
  userRoles: UserRole[];
}

/**
 * Keyed by role, not by user, so a second admin on the same browser would inherit
 * the first one's chosen view. Exported so the sign-out teardown can clear them.
 * See clearClientSessionState.
 */
export const DASHBOARD_VIEW_ADMIN_KEY = 'dashboard-view-admin';
export const DASHBOARD_VIEW_KITCHEN_KEY = 'dashboard-view-kitchen';

export function DashboardViewProvider({
  children,
  userRoles,
}: DashboardViewProviderProps): JSX.Element {
  const isAdmin = userRoles.includes(UserRole.admin);
  const isKitchen = userRoles.includes(UserRole.kitchen);
  const canSwitchViews = isAdmin || isKitchen;

  // Compute available views based on user role
  const availableViews: UserRole[] = isAdmin
    ? [UserRole.admin, UserRole.kitchen, UserRole.employee]
    : isKitchen
      ? [UserRole.kitchen, UserRole.employee]
      : [];

  const [currentDashboardView, setCurrentDashboardView] = useState<UserRole>(() => {
    // Default to highest priority role
    if (isAdmin) return UserRole.admin;
    if (isKitchen) return UserRole.kitchen;
    return userRoles[0] || UserRole.employee;
  });

  // Persist view preference in localStorage (separate keys for admin vs kitchen)
  useEffect(() => {
    if (canSwitchViews) {
      const storageKey = isAdmin ? DASHBOARD_VIEW_ADMIN_KEY : DASHBOARD_VIEW_KITCHEN_KEY;
      const savedView = localStorage.getItem(storageKey) as UserRole;
      if (savedView && availableViews.includes(savedView)) {
        setCurrentDashboardView(savedView);
      }
    }
  }, [canSwitchViews, isAdmin]);

  const handleSetCurrentDashboardView = (view: UserRole): void => {
    if (canSwitchViews && availableViews.includes(view)) {
      setCurrentDashboardView(view);
      const storageKey = isAdmin ? DASHBOARD_VIEW_ADMIN_KEY : DASHBOARD_VIEW_KITCHEN_KEY;
      localStorage.setItem(storageKey, view);
    }
  };

  return (
    <DashboardViewContext.Provider
      value={{
        currentDashboardView,
        setCurrentDashboardView: handleSetCurrentDashboardView,
        isDashboardViewSwitchingEnabled: canSwitchViews,
        availableViews,
      }}
    >
      {children}
    </DashboardViewContext.Provider>
  );
}

export function useDashboardView(): DashboardViewContextType {
  const context = useContext(DashboardViewContext);
  if (context === undefined) {
    throw new Error('useDashboardView must be used within a DashboardViewProvider');
  }
  return context;
}
